import { InfoCircle, Ruler } from "@untitledui/icons";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSampleRulesCount } from "@/api/sample-rules";
import { Button } from "@/components/base/buttons/button";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { SampleRulesDrawer } from "@/components/sample-requests/sample-rules-drawer";
import { cx } from "@/utils/cx";
import type { CreateCollabFormValues } from "../create-collab-group-modal";

export const SamplesStep3 = () => {
  const { watch, setValue } = useFormContext<CreateCollabFormValues>();
  const { data: rulesCountData } = useSampleRulesCount();
  const rulesCount = rulesCountData?.total ?? 0;
  const [rulesDrawerOpen, setRulesDrawerOpen] = useState(false);

  const offerFreeSamples = watch("offerFreeSamples");
  const approvalRule = watch("approvalRule");

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-base font-semibold text-primary">Free Samples</p>
        <p className="text-sm text-quaternary mt-1">
          Control whether creators can request samples, and set the rules for auto-approval
        </p>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-primary bg-secondary px-4 py-3">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-primary">Offer Free Samples</span>
          <Tooltip title="Allow creators to request free product samples" arrow placement="bottom">
            <TooltipTrigger>
              <InfoCircle className="size-3.5 text-fg-quaternary" />
            </TooltipTrigger>
          </Tooltip>
        </div>
        <Toggle size="md" isSelected={offerFreeSamples} onChange={(val) => setValue("offerFreeSamples", val)} />
      </div>

      {offerFreeSamples && (
        <>
          <hr className="border-primary" />
          <div className="flex flex-col gap-6">
            <p className="text-base font-semibold text-primary">Approval rules</p>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setValue("approvalRule", "auto-approve")}
                className="flex flex-1 flex-col items-start gap-2"
              >
                <div
                  className={cx(
                    "w-full rounded-xl border-2 p-12 transition-colors bg-secondary",
                    approvalRule === "auto-approve" ? "border-brand" : "border-primary hover:border-secondary",
                  )}
                  style={{
                    background: "url(/collaborations/Autoapprove.png) no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                  }}
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-primary">Unbox Auto Approve requests</span>
                  <span
                    className={cx(
                      "flex size-4 items-center justify-center rounded-full border-2 transition-colors",
                      approvalRule === "auto-approve" ? "border-brand" : "border-tertiary",
                    )}
                  >
                    {approvalRule === "auto-approve" && <span className="size-2 rounded-full bg-brand-solid" />}
                  </span>
                </div>
                <p className="text-sm text-tertiary">Follows the sample rules that are setup and enabled.</p>
              </button>
              <button
                type="button"
                onClick={() => setValue("approvalRule", "manually-review")}
                className="flex flex-1 flex-col items-start gap-2"
              >
                <div
                  className={cx(
                    "w-full rounded-xl border-2 p-12 transition-colors bg-secondary",
                    approvalRule === "manually-review" ? "border-brand" : "border-primary hover:border-secondary",
                  )}
                  style={{
                    background: "url(/collaborations/Manual.png) no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                  }}
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-primary">Manually review</span>
                  <span
                    className={cx(
                      "flex size-4 items-center justify-center rounded-full border-2 transition-colors",
                      approvalRule === "manually-review" ? "border-brand" : "border-tertiary",
                    )}
                  >
                    {approvalRule === "manually-review" && <span className="size-2 rounded-full bg-brand-solid" />}
                  </span>
                </div>
                <p className="text-sm text-tertiary">
                  This collaboration will be excluded from your Unbox Auto Approval Rules, every sample request will be
                  reviewed individually.
                </p>
              </button>
            </div>

            {approvalRule === "auto-approve" && (
              <div className="flex items-center justify-between gap-4 rounded-lg bg-unbox-cream-50 px-4 py-3">
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-semibold text-primary">
                    {rulesCount === 0 ? "No Rules Active" : `${rulesCount} Rule${rulesCount === 1 ? "" : "s"} Active`}
                  </p>
                  {rulesCount > 0 && (
                    <p className="text-sm text-tertiary">Applied to every collab in this workspace.</p>
                  )}
                </div>
                <Button color="secondary" iconLeading={Ruler} onClick={() => setRulesDrawerOpen(true)}>
                  {rulesCount === 0 ? (
                    "Create Rules"
                  ) : (
                    <>
                      Configure Rules <span className="text-brand-secondary">({rulesCount})</span>
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* This step already lives inside a z-[120] modal overlay; push the drawer above it. */}
      <SampleRulesDrawer open={rulesDrawerOpen} onOpenChange={setRulesDrawerOpen} overlayClassName="z-[130]!" />
    </div>
  );
};
